import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { FirebaseApp } from 'firebase/app';
import { AppCheck, CustomProvider, ReCaptchaEnterpriseProvider, initializeAppCheck } from 'firebase/app-check';

import type { BackendProvider } from '../../types';
import { kzDetectPlatform } from '@shared/services/platform-capability.service';
import { kzMark } from '@shared/util/kz-perf';
import { kzLocalStorage } from './firestore-init';
import { kzDesktopTokenSource } from './app-check-desktop';
import { KzNativeAppCheckStart, kzNativeAppCheckPlan, kzNativeTokenSource } from './app-check-native';
import { KzAppCheckCoolDown, KZ_APP_CHECK_WAIT_MS, kzAppCheckReconnected, kzAppCheckWithin } from './app-check-wait';

export { KZ_APP_CHECK_COOL_CAP_MS, KZ_APP_CHECK_COOL_START_MS, KZ_APP_CHECK_WAIT_MS } from './app-check-wait';

/**
 * App Check on the web shell (kzd-ac AC-1, PLAN-appcheck §4.1): every call to Firestore, RTDB, Storage and the callables
 * carries a short-lived token that only the genuine app gets. Report-only for now — the server refuses nothing until
 * AC-4's `enforce` (PLAN §5) — so this file only has to get tokens flowing, and never get in the way of start-up.
 *
 * Who starts it, decided once at start-up (`kzAppCheckPlan`):
 * - **the web** (a browser; not Android, iOS or the Electron desktop app): reCAPTCHA Enterprise, with this project's web
 *   key (`environment.appCheck.siteKey`). No key — the owner has not created it yet (PLAN §7) — starts nothing.
 * - **Android** gets Play Integrity through the native App Check plugin (AC-2, `app-check-native.ts`) once the build
 *   carries its native Firebase config (`google-services.json`); without it, nothing starts. **iOS** waits until it
 *   ships (ruling AC-R5). **The desktop** gets a token minted for its registered device key (AC-3,
 *   `app-check-desktop.ts`), whether or not the web has a key — only where `environment.appCheck.desktop` is on
 *   (review M1; off everywhere until the owner sets the desktop app up): off, it starts nothing. A WebView on
 *   `https://localhost` or `capacitor-electron://` is not a site reCAPTCHA can vouch for, so these shells never use it.
 * - **a developer's browser** (`environment.appCheck.allowDebug`, dev and the local default only): a debug token kept
 *   under `KZ_APP_CHECK_DEBUG_KEY` in this browser's storage starts it in debug mode — on any shell, key or no key, and
 *   reCAPTCHA is never loaded. The token is registered by hand in the Firebase console (PLAN §7.5); it is never in a
 *   build (check-app-check-debug keeps `allowDebug` false in stage and prod, and the SDK's debug global out of every
 *   other file under `src/`).
 * - **the mock** (or any backend but Firebase): never — there is no Firebase app to protect.
 */

/** Where a developer's browser keeps its App Check debug token (`localStorage`, set by hand; read only when allowed). */
export const KZ_APP_CHECK_DEBUG_KEY = 'kz.appcheck.debug';

/**
 * The SDK's debug switch, read once by `initializeAppCheck`: a string is the debug token, `true` makes the SDK invent
 * one, anything else is no debug mode. Named here and nowhere else under `src/` (check-app-check-debug).
 */
export const KZ_APP_CHECK_DEBUG_GLOBAL = 'FIREBASE_APPCHECK_DEBUG_TOKEN';

/** The reCAPTCHA Enterprise script — the SDK's own `RECAPTCHA_ENTERPRISE_URL`, loaded here instead (see below). */
export const KZ_RECAPTCHA_ENTERPRISE_URL = 'https://www.google.com/recaptcha/enterprise.js';

/** `environment.appCheck` (kzd-ac AC-1). */
export interface KzAppCheckConfig {
  readonly siteKey: string | null;
  readonly allowDebug: boolean;
  /**
   * The desktop's minted tokens (AC-3; wave-2 review M1): only when `true` does the Electron desktop start App Check
   * (and register itself, `DesktopRegistrationService`). False — every env until the owner registers the desktop app
   * and sets `APP_CHECK_DESKTOP_APP_ID` — the desktop starts nothing (`why: 'desktop-off'`), as before AC-3.
   */
  readonly desktop?: boolean;
}

/** What this start does about App Check: start it (and how), or not (and why — for the spec and the console). */
export type KzAppCheckPlan =
  | { readonly start: false; readonly why: 'backend' | 'shell' | 'no-key' | 'no-native' | 'desktop-off' }
  | KzAppCheckStart;

export type KzAppCheckStart =
  | { readonly start: true; readonly mode: 'recaptcha'; readonly siteKey: string }
  | { readonly start: true; readonly mode: 'debug'; readonly debugToken: string }
  | KzNativeAppCheckStart
  | { readonly start: true; readonly mode: 'desktop' };

/**
 * The SDK functions and classes this file uses, read at call time so a spec can stub them (as `KZ_FIRESTORE_SDK`):
 * never the AngularFire zone wrappers.
 */
export const KZ_APP_CHECK_SDK = {
  initializeAppCheck,
  CustomProvider,
  ReCaptchaEnterpriseProvider,
};

/**
 * Decide this start's App Check (see the file comment). A plain function: `app.module.ts` reads it when the root
 * module is declared, so `provideAppCheck` is in the providers only when something starts. The arguments after the
 * backend are seams for the spec.
 */
export function kzAppCheckPlan(
  config: KzAppCheckConfig | null | undefined,
  backend: BackendProvider,
  platform: string = kzDetectPlatform(),
  storage: Storage | null = kzLocalStorage(),
): KzAppCheckPlan {
  if (backend !== 'firebase') return { start: false, why: 'backend' };
  /* kzd-ac AC-2: Android's native plugin (its debug provider under the same rule), or nothing without native config. */
  const native = kzNativeAppCheckPlan(platform, config?.allowDebug === true && readDebugToken(storage) !== null);
  if (native) return native;
  if (config?.allowDebug === true) {
    const debugToken = readDebugToken(storage);
    if (debugToken) return { start: true, mode: 'debug', debugToken };
  }
  /* kzd-ac AC-3: the Electron desktop — a token minted for its registered device key (app-check-desktop.ts) — once
     the env turns it on (review M1); until then, nothing. */
  if (platform === 'electron') return config?.desktop === true ? { start: true, mode: 'desktop' } : { start: false, why: 'desktop-off' };
  /* iOS starts nothing until it ships (ruling AC-R5; app-check-native.ts). */
  if (platform !== 'web') return { start: false, why: 'shell' };
  const siteKey = typeof config?.siteKey === 'string' ? config.siteKey.trim() : '';
  return siteKey ? { start: true, mode: 'recaptcha', siteKey } : { start: false, why: 'no-key' };
}

/** The debug token in this browser's storage, or `null` (none, blank, or storage blocked). Never throws. */
function readDebugToken(storage: Storage | null): string | null {
  try {
    const v = storage?.getItem(KZ_APP_CHECK_DEBUG_KEY);
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Start App Check for `app` (called by `provideAppCheck` in `app.module.ts`, before Firestore, RTDB, Auth and Functions
 * are built: AngularFire makes each of them wait for App Check). Never throws: a start that fails is logged and the
 * app runs on, its calls without a token.
 *
 * - **The SDK's debug global is set here, whatever the plan**: the debug token in debug mode, `false` otherwise.
 *   AngularFire's `provideAppCheck` sets it to `true` on its own on `localhost` or in a dev-mode build, just before it
 *   calls this — that would make the SDK invent a debug token and skip reCAPTCHA (on the dev site too). This file
 *   alone decides.
 * - **Debug mode** never asks its provider (the SDK swaps the debug token for an App Check token itself), so it gets a
 *   `CustomProvider` that loads nothing — not reCAPTCHA, which would fail on `localhost` anyway.
 * - **reCAPTCHA** is the SDK's `ReCaptchaEnterpriseProvider`, wrapped (`kzRecaptchaTokenSource`): on its own it adds
 *   the script with no error handler, so a start offline or with the script blocked leaves its first token request
 *   waiting forever — and Firestore waits for that token before it connects, the callables before they send. Wrapped,
 *   a request that cannot get a token fails within `KZ_APP_CHECK_WAIT_MS`, and the failure is remembered (a growing
 *   cool-down, review WEB-2) so the next requests fail at once instead of waiting again: the SDK then sends without a
 *   token, and its refresher tries again later, loading the script anew once the line is back.
 * - **Android** (`native`) and **the desktop** (`desktop`) get their own sources behind the same `CustomProvider`
 *   (`kzNativeTokenSource`, `kzDesktopTokenSource`), bounded and cooled down alike (app-check-wait.ts).
 * - `isTokenAutoRefreshEnabled`: the token is fetched at start and renewed before it expires (ruling AC-R2: 1 h on the
 *   web), so a call rarely waits for one.
 */
export function createKzAppCheck(
  app: FirebaseApp,
  plan: KzAppCheckStart,
  load: (fresh: boolean) => Promise<void> = (fresh) => kzLoadRecaptchaEnterprise(document, fresh),
  waitMs: number = KZ_APP_CHECK_WAIT_MS,
): AppCheck | null {
  const sdk = KZ_APP_CHECK_SDK;
  (globalThis as Record<string, unknown>)[KZ_APP_CHECK_DEBUG_GLOBAL] = plan.mode === 'debug' ? plan.debugToken : false;
  try {
    const getToken =
      plan.mode === 'debug'
        ? () => Promise.reject(new Error('App Check debug mode exchanges the debug token itself; this is never asked.'))
        : plan.mode === 'native'
          ? kzNativeTokenSource(plan, { waitMs })
          : plan.mode === 'desktop'
            ? kzDesktopTokenSource(app, { waitMs })
            : kzRecaptchaTokenSource(app, plan.siteKey, { load, waitMs });
    const appCheck = sdk.initializeAppCheck(app, { provider: new sdk.CustomProvider({ getToken }), isTokenAutoRefreshEnabled: true });
    kzAppCheckWatchReconnects();
    kzMark('appcheck', plan.mode);
    console.info(`[kz] App Check: ${plan.mode}`);
    return appCheck;
  } catch (err) {
    console.warn('[kz] App Check did not start; calls go without its token.', err);
    return null;
  }
}

/**
 * Where a reconnect is heard (a seam for the spec): the window, and — on a native shell, where a WebView's `online`
 * event is not reliable (as `ConnectivityService` notes) — Capacitor's Network plugin.
 */
export const KZ_APP_CHECK_RECONNECT = {
  window: (): Pick<Window, 'addEventListener' | 'removeEventListener'> | null => (typeof window === 'undefined' ? null : window),
  isNative: (): boolean => Capacitor.isNativePlatform(),
  network: Network as Pick<typeof Network, 'addListener'>,
};

/** How to stop hearing reconnects, while they are heard (once per page). */
let unwatchReconnects: (() => void) | null = null;

/**
 * kzd-ac wave-2 review M5: end every source's cool-down when the line comes back (`kzAppCheckReconnected`) — the
 * window's `online` event, and on Android (any native shell) the Network plugin's status change to connected. Once
 * per page, from `createKzAppCheck`. Never throws.
 */
export function kzAppCheckWatchReconnects(): void {
  if (unwatchReconnects) return;
  const target = KZ_APP_CHECK_RECONNECT.window();
  const online = (): void => kzAppCheckReconnected();
  let native: PluginListenerHandle | null = null;
  unwatchReconnects = () => {
    target?.removeEventListener('online', online);
    void native?.remove();
  };
  try {
    target?.addEventListener('online', online);
    if (KZ_APP_CHECK_RECONNECT.isNative()) {
      void KZ_APP_CHECK_RECONNECT.network
        .addListener('networkStatusChange', (status) => {
          if (status.connected) kzAppCheckReconnected();
        })
        .then((h) => (native = h), () => undefined);
    }
  } catch {
    /* no reconnect to hear: the cool-downs run their course */
  }
}

/** Stop hearing reconnects (for the specs: each starts from none). */
export function kzAppCheckUnwatchReconnects(): void {
  unwatchReconnects?.();
  unwatchReconnects = null;
}

/** A token as `CustomProvider` takes it (the SDK's `AppCheckToken`). */
interface KzAppCheckToken {
  readonly token: string;
  readonly expireTimeMillis: number;
}

/**
 * The SDK's provider as it is driven inside the SDK (`@internal` there: not in its typings). A canary spec pins the
 * shape on the real prototype (review WEB-3); `hasInternalProviderShape` checks it again at run time.
 */
interface KzInternalProvider {
  initialize(app: FirebaseApp): void;
  getToken(): Promise<KzAppCheckToken>;
}

/** After this many token requests in a row that reCAPTCHA did not answer in time, it is rebuilt from a fresh script. */
export const KZ_APP_CHECK_MAX_TIMEOUTS = 3;

/** The provider still has the two methods this file drives (review WEB-3): an SDK upgrade may rename them. */
export function hasInternalProviderShape(p: unknown): p is KzInternalProvider {
  const it = p as Partial<KzInternalProvider> | null;
  return !!it && typeof it.initialize === 'function' && typeof it.getToken === 'function';
}

let warnedNoInternal = false;
/** The one-time warning when the SDK's provider lost its shape (review WEB-3). Exported for the spec. */
export function warnNoInternalProvider(): void {
  if (warnedNoInternal) return;
  warnedNoInternal = true;
  kzMark('appcheck', 'no-internal-provider');
  console.warn('[kz] App Check: the SDK\'s reCAPTCHA Enterprise provider has changed shape; calls go without its token.');
}

export interface KzRecaptchaTokenSourceOptions {
  /** Load the script; `fresh`: throw away the one there (and reCAPTCHA with it) first. */
  readonly load: (fresh: boolean) => Promise<void>;
  /** How long the script, then reCAPTCHA's answer, may take (`KZ_APP_CHECK_WAIT_MS`). */
  readonly waitMs: number;
  /** The clock, for the spec. */
  readonly now?: () => number;
}

/**
 * The reCAPTCHA Enterprise token source behind the web's `CustomProvider` (see `createKzAppCheck`). Per request:
 * 1. during a cool-down (below), it rejects at once;
 * 2. the SDK's own `ReCaptchaEnterpriseProvider` is made (it does nothing yet) and its shape checked (review WEB-3):
 *    without `initialize`/`getToken` it warns once and every request rejects at once — calls go without a token;
 * 3. the script, loaded by `load` (once; a load that failed is tried again by the next request; one still on its way
 *    is waited for again) — at most `waitMs`;
 * 4. then the provider is set up for the app (it finds reCAPTCHA loaded and adds no second script) and asked for the
 *    token — at most `waitMs`.
 *
 * A failure is remembered (review WEB-2): after a load that failed or timed out, or a token that timed out, the next
 * requests reject at once for a cool-down — 60 s, doubling to 10 min, back to nothing on the first token. Without it
 * every request (Firestore's, each callable's) would wait the full `waitMs` again behind a blocked script or a captive
 * portal. After `KZ_APP_CHECK_MAX_TIMEOUTS` token requests in a row that timed out, the provider is thrown away (its
 * hidden widget too) and the next try rebuilds it on a fresh script.
 */
export function kzRecaptchaTokenSource(
  app: FirebaseApp,
  siteKey: string,
  opts: KzRecaptchaTokenSourceOptions,
): () => Promise<KzAppCheckToken> {
  const now = opts.now ?? (() => Date.now());
  let inner: KzInternalProvider | null = null;
  let ready = false; // `inner` set up for the app
  let loading: Promise<void> | null = null;
  let fresh = false;
  let unusable = false;
  const cool = new KzAppCheckCoolDown(now);
  let timeouts = 0;

  return async () => {
    if (unusable) throw new Error('App Check: no usable reCAPTCHA Enterprise provider.');
    if (cool.cooling) throw new Error('App Check: reCAPTCHA failed a moment ago; not asked again yet.');
    if (!inner) {
      const made: unknown = new KZ_APP_CHECK_SDK.ReCaptchaEnterpriseProvider(siteKey);
      if (!hasInternalProviderShape(made)) {
        unusable = true;
        warnNoInternalProvider();
        throw new Error('App Check: no usable reCAPTCHA Enterprise provider.');
      }
      inner = made;
      ready = false;
    }
    if (!ready) {
      if (!loading) {
        const attempt: Promise<void> = opts.load(fresh).catch((err: unknown) => {
          if (loading === attempt) loading = null;
          throw err;
        });
        loading = attempt;
        fresh = false;
      }
      try {
        await kzAppCheckWithin(loading, opts.waitMs, 'The reCAPTCHA script did not load in time.');
      } catch (err) {
        cool.fail();
        throw err;
      }
      if (!ready) {
        inner.initialize(app);
        ready = true;
      }
    }
    let timedOut = false;
    try {
      const token = await kzAppCheckWithin(inner.getToken(), opts.waitMs, 'reCAPTCHA did not answer in time.', () => (timedOut = true));
      cool.reset();
      timeouts = 0;
      return token;
    } catch (err) {
      if (timedOut) {
        cool.fail();
        if (++timeouts >= KZ_APP_CHECK_MAX_TIMEOUTS) {
          /* Rebuilt on a fresh script: the widget the SDK added for this app goes with it. */
          inner = null;
          ready = false;
          loading = null;
          fresh = true;
          timeouts = 0;
          globalThis.document?.getElementById(`fire_app_check_${app.name}`)?.remove();
        }
      }
      throw err;
    }
  };
}

/**
 * Load the reCAPTCHA Enterprise script: resolves once `grecaptcha.enterprise` is there (at once if it already is),
 * rejects when the script fails (offline, blocked) — its tag is then removed, so the next try adds a fresh one.
 * `fresh` (a rebuild, review WEB-2): the script already there and its `grecaptcha` are thrown away first.
 */
export function kzLoadRecaptchaEnterprise(doc: Document = document, fresh = false): Promise<void> {
  const g = globalThis as { grecaptcha?: { enterprise?: unknown } };
  const ready = () => !!g.grecaptcha?.enterprise;
  if (fresh) {
    doc.head.querySelectorAll(`script[src="${KZ_RECAPTCHA_ENTERPRISE_URL}"]`).forEach((el) => el.remove());
    delete g.grecaptcha;
  } else if (ready()) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    const script = doc.createElement('script');
    script.src = KZ_RECAPTCHA_ENTERPRISE_URL;
    script.async = true;
    script.onload = () => (ready() ? resolve() : reject(new Error('The reCAPTCHA script loaded without reCAPTCHA Enterprise.')));
    script.onerror = () => {
      script.remove();
      reject(new Error('The reCAPTCHA script did not load (offline, or blocked).'));
    };
    doc.head.appendChild(script);
  });
}
